import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-hot-toast";
import { Button, Card, Col, Row, Spinner } from "react-bootstrap";
import WebinarService from "../../Services/WebinarService";
import AddWebinar from "./AddWebinar";
import EditWebinar from "./EditWebinar";

function MainEventsWebinars({ pageTitle = "Events and Webinars" }) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [webinars, setWebinars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [editingWebinar, setEditingWebinar] = useState(null);

  const handleShow = () => setShowAddModal(true);
  const handleClose = () => setShowAddModal(false);
  const handleShowEdit = (webinar) => {
    setEditingWebinar(webinar);
    setShowEditModal(true);
  };
  const handleCloseEdit = () => {
    setShowEditModal(false);
    setEditingWebinar(null);
  };

  // Fetch all webinars
  const getWebinars = useCallback(async () => {
    try {
      setLoading(true);
      const response = await WebinarService.getWebinars();
      if (response.data.success) {
        setWebinars(response.data.data || []);
      } else {
        toast.error(response.data.message || "Failed to fetch webinars");
      }
    } catch (error) {
      console.error("Error fetching webinars:", error);
      toast.error("An error occurred while fetching webinars");
    } finally {
      setLoading(false);
    }
  }, []);

  // Delete webinar
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this webinar?")) {
      return;
    }

    try {
      setDeleting(id);
      const response = await WebinarService.deleteWebinar(id);
      if (response.data.success) {
        toast.success("Webinar deleted successfully");
        getWebinars(); // Refresh the list
      } else {
        toast.error(response.data.message || "Failed to delete webinar");
      }
    } catch (error) {
      console.error("Error deleting webinar:", error);
      toast.error("An error occurred while deleting webinar");
    } finally {
      setDeleting(null);
    }
  };

  // Handle successful webinar creation
  const handleWebinarCreated = () => {
    getWebinars(); // Refresh the list
  };

  // Handle successful webinar update
  const handleWebinarUpdated = () => {
    getWebinars(); // Refresh the list
  };

  useEffect(() => {
    getWebinars();
  }, [getWebinars]);

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "200px" }}
      >
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <>
      <div className="mb-4">
        <div className="d-flex flex-column flex-md-row align-items-center justify-content-between gap-3">
          <div className="flex-grow-1 d-flex justify-content-center">
            <h1
              className="fw-bold text-center mb-0"
              style={{ letterSpacing: "1px" }}
            >
              {pageTitle}
            </h1>
          </div>
          <div>
            <Button
              variant="primary"
              onClick={handleShow}
              className="py-2 px-4 fs-5 rounded-pill shadow-sm"
              style={{ minWidth: "220px" }}
            >
              <i className="bi bi-plus-circle me-2"></i>
              Add New {pageTitle}
            </Button>
          </div>
        </div>
      </div>

      {webinars.length === 0 ? (
        <div className="text-center py-5">
          <h4 className="text-muted mb-3">No {pageTitle} found</h4>
          <p className="text-muted mb-4">
            Click <span className="fw-semibold">"Add New {pageTitle}"</span> to
            create your first {pageTitle}.
          </p>
        </div>
      ) : (
        <Row className="g-4">
          {webinars.map((webinar) => (
            <Col key={webinar.id} xs={12} sm={6} md={4} lg={3}>
              <Card
                className="h-100 border-3 border-primary shadow-sm"
                style={{
                  borderRadius: "1.25rem",
                  borderWidth: "3px",
                  borderStyle: "solid",
                  background:
                    "linear-gradient(135deg, #e3f0ff 0%, #f8f9fa 100%)",
                  transition: "transform 0.15s cubic-bezier(.4,2,.6,1)",
                  transform: "scale(1)",
                }}
              >
                <div
                  style={{
                    height: "210px",
                    background:
                      "linear-gradient(135deg, #d0e6ff 0%, #f8f9fa 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderTopLeftRadius: "1.25rem",
                    borderTopRightRadius: "1.25rem",
                    overflow: "hidden",
                    borderBottom: "2px solid #0d6efd",
                  }}
                >
                  <Card.Img
                    variant="top"
                    src={webinar.imagePath}
                    alt={webinar.title || "Webinar"}
                    style={{
                      height: "100%",
                      width: "100%",
                      objectFit: "cover",
                      filter: "contrast(1.08) saturate(1.1)",
                      borderTopLeftRadius: "1.25rem",
                      borderTopRightRadius: "1.25rem",
                    }}
                  />
                </div>
                <Card.Body className="d-flex flex-column p-4">
                  <Card.Title
                    className="fw-bold text-primary-emphasis mb-2"
                    style={{
                      fontSize: "16px",
                      letterSpacing: "0.5px",
                      textShadow: "0 1px 2px #e3f0ff",
                    }}
                  >
                    {webinar.title || "Untitled {pageTitle}"}
                  </Card.Title>

                  {webinar.location && (
                    <Card.Text
                      className="text-muted mb-2"
                      style={{
                        fontSize: "14px", // Increased from 0.9rem
                        lineHeight: "1.4",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      <i className="bi bi-geo-alt me-2"></i>
                      {webinar.location}
                    </Card.Text>
                  )}

                  {webinar.description && (
                    <Card.Text
                      className="text-muted mb-3"
                      style={{
                        fontSize: "14px ",
                        lineHeight: "1.4",
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      <i className="bi bi-file-text me-2"></i>
                      {webinar.description}
                    </Card.Text>
                  )}

                  <Card.Text
                    className="fw-bold text-primary-emphasis mb-3"
                    style={{
                      fontSize: "14px", // Increased from 0.9rem
                      letterSpacing: "0.5px",
                      textShadow: "0 1px 2px #e3f0ff",
                    }}
                  >
                    <i className="bi bi-calendar-event me-2"></i>
                    <b>Date: {new Date(webinar.date).toLocaleDateString()}</b>
                  </Card.Text>

                  {webinar.type && (
                    <Card.Text className="mb-3" style={{ fontSize: "14px" }}>
                      Event Theme: {webinar.type}
                    </Card.Text>
                  )}

                  <div className="mt-auto">
                    <div className="d-flex justify-content-end gap-3">
                      <Button
                        variant="primary"
                        onClick={() => handleShowEdit(webinar)}
                        style={{
                          padding: "0.4rem 1rem",
                          fontSize: "1.15rem",
                          fontWeight: 600,
                          letterSpacing: "0.5px",
                          boxShadow: "0 2px 8px 0 rgba(13,110,253,0.10)",
                          borderRadius: "2rem",
                        }}
                        className="d-flex align-items-center"
                      >
                        <i className="bi bi-pencil-square me-2"></i>
                        Edit
                      </Button>
                      <Button
                        variant="danger"
                        onClick={() => handleDelete(webinar.id)}
                        disabled={deleting === webinar.id}
                        style={{
                          padding: "0.4rem 1rem",
                          fontSize: "1.15rem",
                          fontWeight: 600,
                          letterSpacing: "0.5px",
                          boxShadow: "0 2px 8px 0 rgba(220,53,69,0.10)",
                          borderRadius: "2rem",
                        }}
                        className="d-flex align-items-center"
                      >
                        {deleting === webinar.id ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Deleting...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-trash me-2"></i>
                            Delete
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <AddWebinar
        show={showAddModal}
        onHide={handleClose}
        onSuccess={handleWebinarCreated}
        pageTitle={pageTitle}
      />

      <EditWebinar
        show={showEditModal}
        onHide={handleCloseEdit}
        onSuccess={handleWebinarUpdated}
        webinarData={editingWebinar}
        pageTitle={pageTitle}
      />
    </>
  );
}

export default MainEventsWebinars;
